/**
 * The Home page's scheduled reports — the calendar and the table beneath it.
 *
 *   POST /v1/scheduler/all   the report scheduler, a separate service. Reached
 *                            through ScheduleReports/scheduleApi.js, which
 *                            already mints the service token it needs and drops
 *                            soft-deleted rows.
 *
 * Nothing new is called and nothing is re-implemented: `fetchSchedules`,
 * `rowFromSchedule` and `upcomingRunDates` are the same functions the Scheduled
 * Reports page uses, so the two screens cannot drift on what a schedule is
 * called, when it runs, or whether it counts as published.
 */

import { fetchSchedules } from "../ScheduleReports/scheduleApi";
import { rowFromSchedule, activeRecurrence } from "../ScheduleReports/scheduleMappers";
import { upcomingRunDates } from "../ScheduleReports/expandRecurrence";

/* How far ahead to work out run dates, and the ceiling per schedule.

   Both are well below expandRecurrence's own defaults (12 months / 400 dates).
   The calendar opens on this month and the marks only matter for the months
   somebody actually pages to, while a daily rule generates one date per day —
   so a wide horizon is a lot of arithmetic on every page load for dots nobody
   scrolls to. Three months covers ordinary navigation. */
const HORIZON_MONTHS = 3;
const MAX_DATES_PER_SCHEDULE = 100;

/** Every schedule for the signed-in user's organization, newest first. */
export async function fetchHomeSchedules() {
  /* The scheduler takes the organization, which login writes to localStorage —
     it is not on the JWT this app decodes. Same source the list page uses. */
  const records = await fetchSchedules(localStorage.getItem("orgID") || "");

  return records.map((record) => ({ ...rowFromSchedule(record), raw: record })).sort((a, b) => b.createdTs - a.createdTs);
}

/* "14:45" -> "02:45 PM". scheduleMappers keeps its own copy private, and this
   needs only the one line of it. */
const to12Hour = (runAt) => {
  const [rawHour, rawMinute] = String(runAt || "").split(":");
  const hour24 = Number(rawHour);
  if (Number.isNaN(hour24)) return "";
  return `${String(((hour24 + 11) % 12) + 1).padStart(2, "0")}:${rawMinute || "00"} ${hour24 >= 12 ? "PM" : "AM"}`;
};

/* Per delivery channel, so a WhatsApp schedule is distinguishable from an email
   one at a glance. Same three channels CreateSchedule offers. */
const CHANNEL_STYLE = {
  Email: { iconBg: "#dbeafe", iconColor: "#2563eb" },
  WhatsApp: { iconBg: "#d1fae5", iconColor: "#059669" },
  SMS: { iconBg: "#fef3c7", iconColor: "#d97706" },
};

const DEFAULT_STYLE = { iconBg: "#e0e7ff", iconColor: "#6366f1" };

/* "YYYY-MM-DD" -> a local Date at midnight.
   Built from the parts rather than parsed: `new Date("2026-09-18")` is read as
   UTC midnight, which lands on the 17th for anyone west of UTC — the calendar
   would mark the wrong day. */
const localDateFrom = (key) => {
  const [year, month, day] = String(key || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * One calendar entry per upcoming run of every schedule.
 *
 * A schedule is a rule, not a date, so each row is expanded into the days it is
 * actually due — `upcomingRunDates` handles all the patterns including Custom,
 * and is the same expansion the Scheduled Reports summary panel shows.
 *
 * Drafts are left out. A draft does not run, so marking days for one would
 * promise deliveries that are never going to happen.
 *
 * ── A CAVEAT WORTH KNOWING ──
 * /v1/scheduler/all returns a partial record: ValidTo, ExecutionLimit and
 * ScheduleEndCondition are missing from it (see the header of scheduleMappers).
 * Those are where a *legacy* schedule keeps its end date, so one that has
 * already finished can still be drawn as though it runs forever. Schedules
 * built in the Custom dialog carry their own `Ends` inside the recurrence block
 * and expand correctly. Fixing the rest needs either those fields on /all or a
 * GET /id per schedule, which is a request per row and not worth it here.
 */
export function calendarItems(rows) {
  const items = [];

  (rows || [])
    .filter((row) => row.status === "Published")
    .forEach((row) => {
      const block = activeRecurrence(row.raw);
      const time = to12Hour(block.RunAt);
      const style = CHANNEL_STYLE[row.scheduleType] || DEFAULT_STYLE;

      const dates = upcomingRunDates(row.raw, {
        horizonMonths: HORIZON_MONTHS,
        maxDates: MAX_DATES_PER_SCHEDULE,
      });

      dates.forEach((key) => {
        const date = localDateFrom(key);
        if (!date) return;

        items.push({
          /* One schedule appears on many days, so the day has to be part of the
             key — the schedule id alone would repeat across rows. */
          id: `${row.id}-${key}`,
          scheduleId: row.id,
          date,
          time,
          title: row.reportName,
          description: row.frequency,
          type: row.scheduleType,
          fileType: row.fileType,
          ...style,
        });
      });
    });

  /* Earliest run first within a day, so the day's list reads down the clock.
     Times are already 12-hour strings by here, so the sort is on the underlying
     RunAt-derived order rather than the string — hence comparing dates first
     and falling back to the title for a stable order at equal times. */
  return items.sort((a, b) => a.date - b.date || a.title.localeCompare(b.title));
}
